import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  FaCalendarAlt, 
  FaMapMarkerAlt, 
  FaBuilding, 
  FaSpinner, 
  FaExclamationCircle, 
  FaCheckCircle, 
  FaReceipt, 
  FaFileInvoiceDollar, 
  FaArrowLeft, 
  FaSave, 
  FaCheck, 
  FaClock, 
  FaInfoCircle,
  FaFilePdf
} from 'react-icons/fa';
import PageHeader from '../../components/PageHeader';
import { FullPageSkeleton } from '../../components/ui/Skeleton';
import { toast } from '../../components/ui/Toast';
import { useRealtimeRefresh } from '../../context/RealtimeSyncContext';

const SUPPORTED_STATES = ['Virginia', 'Minnesota', 'Illinois', 'New Jersey', 'Ohio'];

const SalesTax = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [userData, setUserData] = useState(null);
  const [salesTaxService, setSalesTaxService] = useState(null);
  const [salesTaxSteps, setSalesTaxSteps] = useState([
    { step: 1, title: 'Initial Information Required', status: 'in-progress' },
    { step: 2, title: 'Sales Tax Filed', status: 'pending' }
  ]);

  // Page view for Step 1 Checklist (renders directly on page, NOT a popup modal)
  const [showChecklistForm, setShowChecklistForm] = useState(false);
  const [isSubmittingChecklist, setIsSubmittingChecklist] = useState(false);
  const [isSavingDraft, setIsSavingDraft] = useState(false);

  // 4 Checklist Questions (New Jersey format used for all active states)
  const [formData, setFormData] = useState({
    grossReceipts: '',
    nonTaxableReceipts: '',
    salesTaxCollected: '',
    useTaxDue: '',
    notes: ''
  });

  const fetchSalesTaxData = useCallback(async () => {
    const token = localStorage.getItem('token');
    if (!token) {
      navigate('/login');
      return;
    }

    try {
      setError('');
      const userResponse = await fetch(`${process.env.REACT_APP_API_BASE_URL}/api/auth/user?_t=${Date.now()}`, {
        headers: { Authorization: `Bearer ${token}`, 'Cache-Control': 'no-cache' },
        cache: 'no-store',
      });

      if (!userResponse.ok) throw new Error('Failed to fetch user data');
      const data = await userResponse.json();
      const user = data.user;
      setUserData(user);

      if (user?.salesTaxService) {
        setSalesTaxService(user.salesTaxService);
      }

      // Fetch dedicated sales tax status
      const serviceResponse = await fetch(`${process.env.REACT_APP_API_BASE_URL}/api/sales-tax/service/${user._id}?_t=${Date.now()}`, {
        headers: { Authorization: `Bearer ${token}`, 'Cache-Control': 'no-cache' },
        cache: 'no-store'
      });

      if (serviceResponse.ok) {
        const serviceData = await serviceResponse.json();
        if (serviceData.salesTaxService) {
          setSalesTaxService(serviceData.salesTaxService);
        }
        if (serviceData.salesTaxSteps && serviceData.salesTaxSteps.length > 0) {
          setSalesTaxSteps(serviceData.salesTaxSteps);
          const step1 = serviceData.salesTaxSteps.find(s => s.step === 1);
          if (step1 && step1.answers) {
            setFormData(prev => ({
              ...prev,
              grossReceipts: step1.answers.grossReceipts || '',
              nonTaxableReceipts: step1.answers.nonTaxableReceipts || '',
              salesTaxCollected: step1.answers.salesTaxCollected || '',
              useTaxDue: step1.answers.useTaxDue || '',
              notes: step1.answers.notes || ''
            }));
          }
        }
      }
    } catch (err) {
      console.error('Error fetching sales tax data:', err);
      setError(err.message || 'Unable to load Sales Tax details');
    } finally {
      setLoading(false);
    }
  }, [navigate]);

  useEffect(() => {
    fetchSalesTaxData();
  }, [fetchSalesTaxData]);

  // Realtime synchronization listener
  useRealtimeRefresh(['sales-tax', 'profile'], () => {
    fetchSalesTaxData();
  });

  const stateName = salesTaxService?.state || 'New Jersey';
  const entityType = salesTaxService?.entityType || 'LLC';
  const isNewJersey = (stateName || '').toLowerCase().includes('jersey') || (stateName || '').toLowerCase() === 'nj';

  // Calculate Progress of 4 Questions
  const getStep1Progress = () => {
    const fields = ['grossReceipts', 'nonTaxableReceipts', 'salesTaxCollected', 'useTaxDue'];
    const filledCount = fields.filter(f => formData[f] !== '' && formData[f] !== undefined && formData[f] !== null).length;
    const percent = Math.round((filledCount / 4) * 100);
    return { count: filledCount, total: 4, percent };
  };

  const step1 = salesTaxSteps.find(s => s.step === 1) || { status: 'in-progress' };
  const step2 = salesTaxSteps.find(s => s.step === 2) || { status: 'pending' };

  const isStep1Completed = step1.status === 'submitted' || step1.status === 'completed';
  const progressInfo = isStep1Completed ? { count: 4, total: 4, percent: 100 } : getStep1Progress();

  const handleInputChange = (field, val) => {
    // Clean numeric/decimal input
    const cleaned = val.replace(/[^0-9.]/g, '');
    setFormData(prev => ({ ...prev, [field]: cleaned }));
  };

  const handleSaveDraft = async () => {
    if (!userData?._id) return;
    setIsSavingDraft(true);
    const token = localStorage.getItem('token');

    try {
      const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}/api/sales-tax/draft/${userData._id}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({ draftAnswers: formData })
      });

      if (!response.ok) throw new Error('Failed to save draft');
      toast.success('Draft saved successfully!');
    } catch (err) {
      toast.error('Error saving draft: ' + err.message);
    } finally {
      setIsSavingDraft(false);
    }
  };

  const handleSubmitChecklist = async (e) => {
    if (e && e.preventDefault) e.preventDefault();
    if (!userData?._id) return;

    if (
      formData.grossReceipts === '' ||
      formData.nonTaxableReceipts === '' ||
      formData.salesTaxCollected === '' ||
      formData.useTaxDue === ''
    ) {
      toast.error('Please enter all 4 required checklist figures.');
      return;
    }

    if (!window.confirm('Are you sure you want to submit this Sales Tax information for review?')) {
      return;
    }

    setIsSubmittingChecklist(true);
    const token = localStorage.getItem('token');

    try {
      const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}/api/sales-tax/submit-checklist/${userData._id}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({ answers: formData })
      });

      if (!response.ok) {
        const errData = await response.json().catch(() => ({}));
        throw new Error(errData.message || 'Failed to submit checklist');
      }

      const data = await response.json();
      if (data.salesTaxSteps) {
        setSalesTaxSteps(data.salesTaxSteps);
      }
      setShowChecklistForm(false);
      toast.success('Sales Tax checklist submitted successfully! PDF stored in Documents.');
      fetchSalesTaxData();
    } catch (err) {
      toast.error('Error: ' + err.message);
    } finally {
      setIsSubmittingChecklist(false);
    }
  };

  // Helper formatting for dates
  const formatDateForDisplay = (d) => {
    if (!d) return 'N/A';
    return new Date(d).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  // StepCard Component - Standard BooksComply sizing & aesthetics
  const StepCard = ({ step, title, status, onClick, progressData }) => {
    const isCompleted = status === 'submitted' || status === 'completed';
    const isInProgress = status === 'in-progress';
    const isPending = status === 'pending';

    const getCardStyles = () => {
      switch (status) {
        case 'in-progress':
          return 'bg-[#1e2a3e] border-[#2d3f5a]';
        case 'submitted':
        case 'completed':
          return 'bg-green-600 border-green-500';
        default:
          return 'bg-blue-600 border-blue-500';
      }
    };

    const getBadgeStyles = () => {
      switch (status) {
        case 'in-progress':
          return 'bg-blue-600 text-white';
        case 'submitted':
        case 'completed':
          return 'bg-green-800 text-white';
        default:
          return 'bg-blue-800 text-white';
      }
    };

    const getStatusBadge = () => {
      switch (status) {
        case 'in-progress':
          return 'IN PROGRESS';
        case 'submitted':
          return 'SUBMITTED';
        case 'completed':
          return 'COMPLETED';
        default:
          return 'PENDING';
      }
    };

    const isStep1Disabled = step === 1 && isCompleted;

    return (
      <div 
        onClick={() => {
          if (isStep1Disabled) {
            toast.info('Step 1 checklist has already been submitted.');
            return;
          }
          onClick(step);
        }}
        className={`group relative w-full p-4 sm:p-5 rounded-2xl border transition-all duration-300 flex flex-col h-full ${getCardStyles()} ${
          isStep1Disabled ? 'cursor-default' : 'cursor-pointer hover:shadow-xl hover:-translate-y-1'
        }`}
      >
        <div className="flex items-center justify-between gap-2 mb-3">
          <span className="text-white font-bold text-base sm:text-lg">Step {step}</span>
          <span className={`px-2.5 sm:px-3 py-1 rounded text-xs sm:text-xs font-bold uppercase tracking-wide ${getBadgeStyles()}`}>
            {getStatusBadge()}
          </span>
        </div>

        <h3 className="text-white font-semibold text-sm sm:text-base leading-snug flex-grow">
          {title}
        </h3>

        {/* Progress Bar for Step 1 */}
        {step === 1 && status === 'in-progress' && progressData && (
          <div className="mt-auto pt-3">
            <div className="flex items-center justify-between text-xs text-gray-300 mb-1">
              <span className="tracking-wide">Progress</span>
              <span className="font-semibold">{progressData.count}/{progressData.total} • {progressData.percent}%</span>
            </div>
            <div className="h-1.5 bg-gray-600 rounded-full overflow-hidden">
              <div
                className="h-full rounded-full transition-all duration-500 bg-gray-400"
                style={{ width: `${progressData.percent}%` }}
              />
            </div>
          </div>
        )}

        {isInProgress && step !== 1 && (
          <div className="mt-auto pt-2 flex items-center gap-2 text-xs sm:text-sm text-blue-300">
            <FaSpinner className="w-3 h-3 animate-spin" />
            <span>Working for Employee</span>
          </div>
        )}

        {isCompleted && (
          <div className="mt-auto pt-2 flex items-center gap-2 text-xs sm:text-sm text-green-100">
            <FaCheckCircle className="w-3.5 h-3.5" />
            <span>{step === 1 ? 'Checklist Submitted' : 'Sales Tax Return Filed'}</span>
          </div>
        )}

        {isPending && (
          <div className="mt-auto pt-2 flex items-center gap-2 text-xs sm:text-sm text-blue-100">
            <FaSpinner className="w-3 h-3 animate-spin" />
            <span>Working for Employee</span>
          </div>
        )}

        {/* Tooltip on Hover */}
        <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 px-3 py-2 bg-dark-800 text-white text-xs rounded-xl shadow-xl opacity-0 group-hover:opacity-100 transition-opacity duration-200 pointer-events-none whitespace-nowrap z-20">
          {step === 1 ? (isCompleted ? 'Checklist has already been submitted' : `Click to open ${stateName} Checklist`) : 'Sales tax filing process'}
          <div className="absolute top-full left-1/2 -translate-x-1/2 border-4 border-transparent border-t-dark-800" />
        </div>
      </div>
    );
  };

  if (loading) {
    return (
      <div className="space-y-6">
        <FullPageSkeleton message="Loading Sales Tax details..." />
      </div>
    );
  }

  // DIRECT CHECKLIST VIEW (Full page view matching Annual Report Checklist screenshot, NOT a popup modal)
  if (showChecklistForm && !isStep1Completed && isNewJersey) {
    return (
      <div className="space-y-6 pb-20 animate-fade-in">
        <div className="card p-4 sm:p-6">
          {/* Header with Title, Subtitle, and Back Button */}
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
            <div>
              <h1 className="page-title text-xl sm:text-2xl font-bold text-dark-800 dark:text-white">
                {stateName} Sales Tax Checklist
              </h1>
              <p className="page-subtitle text-xs sm:text-sm text-gray-500 dark:text-gray-400 mt-1">
                Complete sales receipts, exemptions, and tax liability figures for this filing period.
              </p>
            </div>
            <button
              type="button"
              onClick={() => setShowChecklistForm(false)}
              className="inline-flex items-center gap-2 px-4 py-2 rounded-xl border border-gray-200 dark:border-dark-600 text-sm font-medium hover:bg-gray-50 dark:hover:bg-dark-700 transition-colors cursor-pointer"
            >
              <FaArrowLeft className="w-3 h-3" />
              Back
            </button>
          </div>

          <form onSubmit={handleSubmitChecklist} className="space-y-6">
            {/* Info notice */}
            <div className="p-3.5 bg-blue-50 dark:bg-dark-700/60 rounded-xl border border-blue-100 dark:border-dark-600 text-xs text-blue-900 dark:text-blue-300 flex items-start gap-2.5">
              <FaInfoCircle className="w-4 h-4 text-blue-600 shrink-0 mt-0.5" />
              <span>
                Please enter your sales tax figures for this filing period for <b>{stateName}</b> ({entityType}). You can save a draft anytime and submit when ready.
              </span>
            </div>

            {/* 4 Checklist Questions */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {/* 1. Total Gross Receipts */}
              <div>
                <label className="block text-sm sm:text-base font-bold text-gray-900 dark:text-white mb-2">
                  1. Total Gross Receipts <span className="text-red-500">*</span>
                </label>
                <div className="relative">
                  <span className="absolute left-3.5 top-1/2 -translate-y-1/2 text-gray-400 font-bold text-base">$</span>
                  <input
                    type="text"
                    placeholder="0.00"
                    value={formData.grossReceipts}
                    onChange={(e) => handleInputChange('grossReceipts', e.target.value)}
                    className="w-full text-base rounded-xl border border-gray-200 dark:border-dark-600 bg-white dark:bg-dark-800 pl-8 pr-3 py-2.5 text-gray-900 dark:text-white font-medium focus:ring-2 focus:ring-primary-500 outline-none"
                  />
                </div>
              </div>

              {/* 2. Non-taxable Receipts */}
              <div>
                <label className="block text-sm sm:text-base font-bold text-gray-900 dark:text-white mb-2">
                  2. Non-taxable Receipts <span className="text-red-500">*</span>
                </label>
                <div className="relative">
                  <span className="absolute left-3.5 top-1/2 -translate-y-1/2 text-gray-400 font-bold text-base">$</span>
                  <input
                    type="text"
                    placeholder="0.00"
                    value={formData.nonTaxableReceipts}
                    onChange={(e) => handleInputChange('nonTaxableReceipts', e.target.value)}
                    className="w-full text-base rounded-xl border border-gray-200 dark:border-dark-600 bg-white dark:bg-dark-800 pl-8 pr-3 py-2.5 text-gray-900 dark:text-white font-medium focus:ring-2 focus:ring-primary-500 outline-none"
                  />
                </div>
              </div>

              {/* 3. Sales Tax Collected */}
              <div>
                <label className="block text-sm sm:text-base font-bold text-gray-900 dark:text-white mb-2">
                  3. Sales Tax Collected <span className="text-red-500">*</span>
                </label>
                <div className="relative">
                  <span className="absolute left-3.5 top-1/2 -translate-y-1/2 text-gray-400 font-bold text-base">$</span>
                  <input
                    type="text"
                    placeholder="0.00"
                    value={formData.salesTaxCollected}
                    onChange={(e) => handleInputChange('salesTaxCollected', e.target.value)}
                    className="w-full text-base rounded-xl border border-gray-200 dark:border-dark-600 bg-white dark:bg-dark-800 pl-8 pr-3 py-2.5 text-emerald-600 dark:text-emerald-400 font-semibold focus:ring-2 focus:ring-primary-500 outline-none"
                  />
                </div>
              </div>

              {/* 4. Use Tax Due */}
              <div>
                <label className="block text-sm sm:text-base font-bold text-gray-900 dark:text-white mb-2">
                  4. Use Tax Due <span className="text-red-500">*</span>
                </label>
                <div className="relative">
                  <span className="absolute left-3.5 top-1/2 -translate-y-1/2 text-gray-400 font-bold text-base">$</span>
                  <input
                    type="text"
                    placeholder="0.00"
                    value={formData.useTaxDue}
                    onChange={(e) => handleInputChange('useTaxDue', e.target.value)}
                    className="w-full text-base rounded-xl border border-gray-200 dark:border-dark-600 bg-white dark:bg-dark-800 pl-8 pr-3 py-2.5 text-gray-900 dark:text-white font-medium focus:ring-2 focus:ring-primary-500 outline-none"
                  />
                </div>
              </div>
            </div>

            {/* Action buttons */}
            <div className="flex flex-wrap gap-3 mt-8 pt-6 border-t border-gray-100 dark:border-dark-700">
              <button
                type="button"
                onClick={() => setShowChecklistForm(false)}
                className="inline-flex items-center gap-2 px-4 py-2.5 rounded-xl border border-gray-200 dark:border-dark-600 text-sm font-medium text-gray-600 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-dark-700 transition-colors cursor-pointer"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleSaveDraft}
                disabled={isSavingDraft || isSubmittingChecklist}
                className="inline-flex items-center gap-2 px-5 py-2.5 rounded-xl border border-gray-200 dark:border-dark-600 text-sm font-semibold hover:bg-gray-50 dark:hover:bg-dark-700 disabled:opacity-50 transition-colors cursor-pointer"
              >
                {isSavingDraft ? <FaSpinner className="animate-spin" /> : <FaSave />}
                Save Draft
              </button>
              <button
                type="submit"
                disabled={isSubmittingChecklist || isSavingDraft}
                className="inline-flex items-center gap-2 px-5 py-2.5 rounded-xl bg-primary-600 hover:bg-primary-700 text-white text-sm font-semibold disabled:opacity-50 transition-colors shadow-md shadow-primary-500/20 cursor-pointer"
              >
                {isSubmittingChecklist ? (
                  <>
                    <FaSpinner className="animate-spin" />
                    <span>Submitting...</span>
                  </>
                ) : (
                  <>
                    <FaCheck />
                    <span>{isStep1Completed ? 'Update Checklist' : 'Submit Checklist'}</span>
                  </>
                )}
              </button>
            </div>
          </form>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6 pb-20">
      {/* Page Header */}
      <PageHeader
        icon={FaReceipt}
        title="Sales tax module"
        subtitle="Manage and track your sales tax compliance, nexus, and filings"
      />

      {/* Error Alert */}
      {error && (
        <div className="p-4 rounded-xl bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800/30 flex items-center justify-between gap-3 text-red-600 dark:text-red-400 text-sm">
          <div className="flex items-center gap-2">
            <FaExclamationCircle className="w-5 h-5 flex-shrink-0" />
            <span>{error}</span>
          </div>
          <button
            onClick={fetchSalesTaxData}
            className="px-3 py-1.5 rounded-lg border border-red-500 hover:bg-red-50 dark:hover:bg-red-900/20 font-medium transition-colors"
          >
            Retry
          </button>
        </div>
      )}

      {/* Sales Tax Due Date Card - Matching Annual Report design */}
      {salesTaxService?.dueDate ? (
        <div className="card overflow-hidden opacity-0 animate-fade-in-up" style={{ animationDelay: '50ms', animationFillMode: 'forwards' }}>
          <div
            className="py-2.5 px-4 text-white flex items-center gap-3"
            style={{ background: 'linear-gradient(135deg, #4f46e5, #9333ea)' }}
          >
            <div className="p-1.5 bg-white/20 rounded-lg backdrop-blur-sm">
              <FaCalendarAlt className="w-3.5 h-3.5" />
            </div>
            <div className="flex-1 flex items-center justify-between">
              <h2 className="text-sm font-bold tracking-tight leading-none">Sales Tax Due Date</h2>
              {(salesTaxService.emailStatus || salesTaxService.reminderCount > 0) && (
                <span className="px-2 py-0.5 rounded text-[10px] font-bold uppercase tracking-wider bg-white/20 text-white backdrop-blur-xs flex items-center gap-1">
                  <FaCheck className="w-2.5 h-2.5" /> Email Status: {salesTaxService.emailStatus || 'Sent'}
                </span>
              )}
            </div>
          </div>

          <div className="p-2.5 md:p-3">
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-2.5 md:gap-3">
              {/* STATE */}
              <div className="flex items-center gap-2.5 p-2.5 rounded-xl bg-gray-50 dark:bg-dark-700/50 border border-gray-100 dark:border-dark-600 transition-all hover:shadow-md group">
                <div className="w-8 h-8 rounded-lg bg-primary-500/10 flex items-center justify-center text-primary-500 group-hover:scale-110 transition-transform flex-shrink-0">
                  <FaMapMarkerAlt className="w-3.5 h-3.5" />
                </div>
                <div className="min-w-0">
                  <p className="text-xs uppercase font-bold text-gray-400 dark:text-gray-500 tracking-widest mb-0.5 truncate">STATE</p>
                  <p className="text-sm font-bold text-dark-800 dark:text-white truncate">{salesTaxService.state || 'New Jersey'}</p>
                </div>
              </div>

              {/* ENTITY TYPE */}
              <div className="flex items-center gap-2.5 p-2.5 rounded-xl bg-gray-50 dark:bg-dark-700/50 border border-gray-100 dark:border-dark-600 transition-all hover:shadow-md group">
                <div className="w-8 h-8 rounded-lg bg-green-500/10 flex items-center justify-center text-green-500 group-hover:scale-110 transition-transform flex-shrink-0">
                  <FaBuilding className="w-3.5 h-3.5" />
                </div>
                <div className="min-w-0">
                  <p className="text-xs uppercase font-bold text-gray-400 dark:text-gray-500 tracking-widest mb-0.5 truncate">ENTITY TYPE</p>
                  <p className="text-sm font-bold text-dark-800 dark:text-white truncate">{salesTaxService.entityType || 'LLC'}</p>
                </div>
              </div>

              {/* REGISTRATION DATE */}
              <div className="flex items-center gap-2.5 p-2.5 rounded-xl bg-gray-50 dark:bg-dark-700/50 border border-gray-100 dark:border-dark-600 transition-all hover:shadow-md group">
                <div className="w-8 h-8 rounded-lg bg-blue-500/10 flex items-center justify-center text-blue-500 group-hover:scale-110 transition-transform flex-shrink-0">
                  <FaCalendarAlt className="w-3.5 h-3.5" />
                </div>
                <div className="min-w-0">
                  <p className="text-xs uppercase font-bold text-gray-400 dark:text-gray-500 tracking-widest mb-0.5 truncate">REGISTRATION DATE</p>
                  <p className="text-sm font-bold text-dark-800 dark:text-white truncate">{formatDateForDisplay(salesTaxService.registrationDate)}</p>
                </div>
              </div>

              {/* DUE DATE */}
              <div className="h-full p-2.5 rounded-xl bg-red-600 text-white text-center shadow-lg shadow-red-600/20 flex flex-col justify-center">
                <p className="text-xs uppercase font-bold tracking-widest opacity-70 mb-0.5">DUE DATE</p>
                <p className="text-base font-bold">{salesTaxService.dueDateText || formatDateForDisplay(salesTaxService.dueDate)}</p>
              </div>
            </div>
          </div>
        </div>
      ) : (
        <div className="card p-4 bg-gradient-to-r from-blue-50/60 to-indigo-50/60 dark:from-blue-900/10 dark:to-indigo-900/10 border border-blue-200/50 dark:border-blue-800/50 flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl bg-blue-500/10 text-blue-600 flex items-center justify-center shrink-0">
            <FaClock className="w-5 h-5" />
          </div>
          <div className="flex-1">
            <h4 className="text-sm font-bold text-dark-800 dark:text-white">State Sales Tax Setup</h4>
            <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
              Your filing due dates and state compliance rules will appear here once configured by our compliance specialist.{isNewJersey ? ' You can proceed with Step 1 below.' : ''}
            </p>
          </div>
        </div>
      )}

      {/* Two Step Cards Section - Only shown for New Jersey where checklist is active */}
      {isNewJersey && (
        <div className="space-y-3">
          <h3 className="text-sm font-bold uppercase tracking-wider text-gray-400 dark:text-gray-500">
            Filing Workflow Steps
          </h3>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 md:gap-6 animate-fade-in-up" style={{ animationDelay: '100ms', minHeight: '200px' }}>
            <div className="h-full">
              <StepCard 
                step={1} 
                title="Initial Information Required"
                status={isStep1Completed ? 'submitted' : 'in-progress'}
                progressData={progressInfo}
                onClick={() => {
                  if (isStep1Completed) {
                    toast.info('Step 1 checklist has already been submitted.');
                    return;
                  }
                  setShowChecklistForm(true);
                }}
              />
            </div>
            <div className="h-full">
              <StepCard 
                step={2} 
                title="Sales Tax Filed"
                status={step2.status === 'completed' ? 'completed' : 'pending'}
                onClick={() => {}}
              />
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default SalesTax;
